/*
 * number-input.js -- the alert builder's numeric fields (the constant to compare
 * with, the target price), and how a number crosses between the form, the
 * browser's locale and the server.
 *
 * What was wrong. The field showed a number in the user's locale (Turkish:
 * 4391 is "4.391"), but the form posted that text as it stood and the server
 * read it under ITS locale (English: "4.391" is 4.391). A Turkish user setting
 * a gold alert at 4,391 stored 4.391 -- an alert that can never fire. The field
 * could not hold a decimal (it stripped every separator and kept an integer),
 * nor a negative number, and values loaded from a saved alert were written back
 * as raw numbers that the next save misread the same way.
 *
 * What holds now.
 *   - The field always holds text in the user's locale: grouped integer part,
 *     the locale's decimal mark, an optional leading minus. formatInput() is
 *     the only thing that writes it, fromNumber() the only way a stored number
 *     gets into it.
 *   - Reading that text back is STRICT (toCanonical): the locale's decimal mark
 *     starts the fraction and every other separator is a group mark. That is
 *     safe because the text is our own output.
 *   - A number is sent to the server in canonical form -- "4391", "0.0226",
 *     "-50.5" -- which means the same in every locale. The server parses that
 *     directly (views.py parse_decimal).
 *   - Text from OUTSIDE (a paste) is read tolerantly instead (mode 'paste'): a
 *     lone separator that cannot be a group mark is a decimal, so "0.0226"
 *     pastes correctly into a Turkish page, while "1.234.567" and "4.391" are
 *     grouped integers there; with both marks present the last one is the
 *     decimal, whichever way round ("1.234,5", "1,234.5").
 *   - A separator the user TYPES is resolved by typedSeparator(): the locale's
 *     own decimal mark makes a decimal; the other one makes a decimal only
 *     where a group mark would be meaningless (nothing but zeros before it), and
 *     is otherwise dropped, so it visibly does not appear instead of silently
 *     changing the value.
 *
 * Pure: the locale is a parameter (undefined = the browser's), so it runs under
 * Node for several locales (app/static/tools/study-parity/number-input.test.mjs).
 */

export const MAX_INTEGER_DIGITS = 15;     // stays exact as a double
export const MAX_FRACTION_DIGITS = 8;     // price precision

const cache = new Map();

/* The locale's decimal and group marks. */
export function separators(locale) {
  const key = String(locale);
  if (!cache.has(key)) {
    const parts = new Intl.NumberFormat(locale).formatToParts(11000.1);
    cache.set(key, {
      decimal: (parts.find((p) => p.type === 'decimal') || {}).value || '.',
      group: (parts.find((p) => p.type === 'group') || {}).value || ','
    });
  }
  return cache.get(key);
}

const MINUS = /^\s*[-−–]/;

function clean(parts) {
  let int = parts.int.replace(/^0+(?=\d)/, '').slice(0, MAX_INTEGER_DIGITS);
  const frac = parts.frac === null ? null : parts.frac.slice(0, MAX_FRACTION_DIGITS);
  return { negative: parts.negative, int: int, frac: frac };
}

/* Strict reading of text that is in the locale's own format. */
function parseStrict(text, locale) {
  const raw = String(text === null || text === undefined ? '' : text);
  const negative = MINUS.test(raw);
  const s = raw.replace(/[^\d.,]/g, '');
  const at = s.indexOf(separators(locale).decimal);
  if (at === -1) return clean({ negative: negative, int: s.replace(/[.,]/g, ''), frac: null });
  return clean({
    negative: negative,
    int: s.slice(0, at).replace(/[.,]/g, ''),
    frac: s.slice(at + 1).replace(/[.,]/g, '')
  });
}

/* Tolerant reading of text from outside (a paste). */
function parsePasted(text, locale) {
  const raw = String(text === null || text === undefined ? '' : text);
  const negative = MINUS.test(raw);
  const s = raw.replace(/[^\d.,]/g, '');
  const { decimal } = separators(locale);
  // both marks present ("1.234,5", "1,234.5"): whichever comes LAST is the
  // decimal, whatever this page's locale is
  const mixed = s.indexOf('.') > -1 && s.indexOf(',') > -1;
  const at = mixed ? Math.max(s.lastIndexOf('.'), s.lastIndexOf(',')) : s.lastIndexOf(decimal);
  if (at > -1) {
    return clean({ negative: negative, int: s.slice(0, at).replace(/[.,]/g, ''), frac: s.slice(at + 1).replace(/[.,]/g, '') });
  }
  const other = decimal === '.' ? ',' : '.';
  const first = s.indexOf(other);
  if (first > -1 && first === s.lastIndexOf(other) && !/^\d{1,3}[.,]\d{3}$/.test(s)) {
    // one lone mark that is not the locale's decimal and is not shaped like a
    // thousands group ("0.0226", "12.5") -- it can only be a decimal
    return clean({ negative: negative, int: s.slice(0, first), frac: s.slice(first + 1) });
  }
  return clean({ negative: negative, int: s.replace(/[.,]/g, ''), frac: null });
}

function parse(text, locale, mode) {
  return mode === 'paste' ? parsePasted(text, locale) : parseStrict(text, locale);
}

/* Parts -> the text the field shows. */
function render(parts, locale) {
  const { decimal } = separators(locale);
  let out = '';
  if (parts.int !== '') {
    out = new Intl.NumberFormat(locale, { maximumFractionDigits: 0, useGrouping: true }).format(Number(parts.int));
  } else if (parts.frac !== null) {
    out = '0';                                // ",5" reads "0,5"
  }
  if (parts.frac !== null) out += decimal + parts.frac;   // keeps "0," and "1,50" while the user is still typing
  if (parts.negative) out = '-' + out;
  return out;
}

/*
 * The field's text after an edit. mode 'strict' (default) is for the field's own
 * text -- typing, deleting; 'paste' is for text from elsewhere.
 */
export function formatInput(text, locale, mode) {
  return render(parse(text, locale, mode), locale);
}

/*
 * The separator the user just typed at `index` of `text` (the caret sits after
 * it). Returns the text with that character resolved: kept if it is the
 * locale's decimal mark, turned into the decimal mark if it cannot be a group
 * mark (only zeros before it), dropped otherwise.
 */
export function typedSeparator(text, index, locale) {
  const typed = text[index];
  if (typed !== '.' && typed !== ',') return text;
  const { decimal } = separators(locale);
  if (typed === decimal) return text;
  const digitsBefore = text.slice(0, index).replace(/\D/g, '').replace(/^0+/, '');
  const replacement = digitsBefore === '' ? decimal : '';
  return text.slice(0, index) + replacement + text.slice(index + 1);
}

/*
 * What a keystroke does, with the caret at the end: the field shows `display`,
 * the user types `char`. Used by the tests; the live field goes through the same
 * two functions with the caret wherever it is.
 */
export function typeInto(display, char, locale) {
  const text = display + char;
  return formatInput(typedSeparator(text, text.length - 1, locale), locale);
}

/* The canonical form -- "4391", "0.0226", "-50.5" -- or '' when there is no number. */
export function toCanonical(text, locale, mode) {
  const p = parse(text, locale, mode);
  if (p.int === '' && (p.frac === null || p.frac === '')) return '';
  const frac = p.frac ? '.' + p.frac.replace(/0+$/, '') : '';
  const body = (p.int === '' ? '0' : p.int) + (frac === '.' ? '' : frac);
  return (p.negative && /[1-9]/.test(body) ? '-' : '') + body;
}

/* A stored number as the field shows it. */
export function fromNumber(value, locale) {
  const n = typeof value === 'number' ? value : Number(String(value));
  if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return '';
  const fixed = Math.abs(n).toFixed(MAX_FRACTION_DIGITS).replace(/\.?0+$/, '');
  const [int, frac] = fixed.split('.');
  return render(clean({ negative: n < 0, int: int, frac: frac === undefined ? null : frac }), locale);
}

/* The number in the field's text, or null. */
export function toNumber(text, locale, mode) {
  const canonical = toCanonical(text, locale, mode);
  return canonical === '' ? null : Number(canonical);
}
